import React from "react";

/**
 * Nova top-level ErrorBoundary.
 *
 * Catches ANY uncaught render / lifecycle exception in the component tree
 * below it and renders a friendly recovery screen instead of a blank/black
 * page. This is critical for iOS PWA + App Store submissions: without it,
 * a single bad `map()` on an unexpected API payload takes the whole app
 * down until the user manually clears state.
 *
 * On error we:
 *   - log via console.error so the browser dev-tools + Sentry pick it up
 *   - keep the user in-app (no white screen of death)
 *   - offer two recoveries: "Try again" (soft reset) and "Reload app" (hard)
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so Sentry / any error tracker in the app picks it up.
    // We deliberately don't reference window.Sentry directly to avoid coupling.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  handleReset = () => {
    // Soft reset — attempt to re-mount the tree without a full page reload.
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    // Hard reload — clears any transient in-memory state; equivalent to force-quitting the app.
    try { window.location.reload(); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="global-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#0a0a0a",
          color: "#fff",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px",
            fontSize: 28,
          }}>
            {/* Subtle alert glyph, not an emoji, matches the Nova aesthetic */}
            <span style={{ color: "#b28a3e" }}>!</span>
          </div>
          <h2 style={{ fontSize: 22, margin: "0 0 10px", letterSpacing: -0.4 }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.65)", marginBottom: 24 }}>
            Nova hit an unexpected error and stopped rendering this screen.
            Your data is safe. Tap try again — if it keeps happening, reload the app.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              data-testid="error-boundary-retry-btn"
              onClick={this.handleReset}
              style={btnPrimary}
            >
              Try again
            </button>
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={btnGhost}
            >
              Reload app
            </button>
          </div>
          {/* Only show technical detail on non-production builds — Apple reviewers
              will flag it if it looks like a dev-tools dump in prod. */}
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre style={{
              marginTop: 24, textAlign: "left", fontSize: 11,
              color: "rgba(255,255,255,0.4)", whiteSpace: "pre-wrap",
              maxHeight: 180, overflow: "auto",
              padding: 12, borderRadius: 8,
              background: "rgba(255,255,255,0.03)",
            }}>
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

const btnPrimary = {
  background: "#b28a3e",
  color: "#0a0a0a",
  border: "none",
  padding: "12px 22px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};

const btnGhost = {
  background: "transparent",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.15)",
  padding: "12px 22px",
  borderRadius: 999,
  fontWeight: 500,
  fontSize: 14,
  cursor: "pointer",
};

export default ErrorBoundary;
